import { Step, StepContent, StepLabel, Stepper } from "@material-ui/core";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import ImageUploader from "react-images-upload";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { translate } from "react-switch-lang";
import { Card, CardBody, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { Language } from "../../../../models/languageDto";
import { TrainingPathCreationDTO } from "../../../../models/trainingPathCreationDTO";
import { TrainingPathDto } from "../../../../models/trainingPathDto";
import { TrainingPathTanslationDto } from "../../../../models/trainingPathTranslationDto";
import { useHttpClient } from "../../../../security/hooks/axiosProvider";
import { ColorlibStepIcon, getFlag } from "../../../../shared/timeline-utils/steps-icons";
import { RootState } from "../../../../store";
import ButtonComponent from "../components/buttonComponent";
import SpinnerComponent from "../components/spinnerComponent";
import styles from "./training-path-manager.module.scss";
import TrainingPathTranslationForm from "./trainingPathTranslationForm";

interface LanguageOptions {
  value: string;
  label: string;
}

const LANGUAGE_OPTIONS: Array<LanguageOptions> = [
  { value: "FR", label: "français" },
  { value: "EN", label: "anglais" },
];

const AddTrainingPathForm: React.FC<{ t: (key: string) => {} }> = props => {
  const httpClient = useHttpClient(true);
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const [image, setImage] = useState(null);
  const [trainingPath, setTrainingPath] = useState<TrainingPathDto>(new TrainingPathDto());
  const [trainingPathsTranslation, setTrainingPathsTranslation] = useState<Array<TrainingPathTanslationDto>>([]);
  const [isDisplayedFrenchForm, setIsDisplayedFrenchForm] = useState<boolean>(false);
  const [isDisplayedEnglishForm, setIsDisplayedEnglishForm] = useState<boolean>(false);
  const [loader, setLoader] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [remainingLang, setRemainingLang] = useState("");
  const [support, setSupport] = useState<Map<string, any>>(null);
  const [imageAccepted, setImageAccepted] = useState<boolean>();
  const [tpTranslationIdToDelete, setTpTranslationIdToDelete] = useState<number>(null);

  useEffect(() => {
    if (params.id) {
      getTrainingPathById();
      getTrainingPathsTranslationByTrainingPath();
    }
    // eslint-disable-next-line
  }, []);

  const currentLang = useSelector((state: RootState) => state.language.currentLang);
  useEffect(() => {}, [currentLang]);

  const deleteTrainingPathImage = (path: string): Promise<void> => {
    return httpClient.current?.delete(`/trainingPath/picture`, {
      params: {
        fullImagePath: path,
      },
    });
  };

  const getTrainingPathById = (): void => {
    httpClient.current?.get(`/trainingPath/${params.id}`).then(response => {
      setTrainingPath(response.data);
    });
  };

  const addSupportToTPS = (): Promise<Array<TrainingPathTanslationDto>> => {
    const promises = new Array<Promise<{ filePath: string; fileUrl: string }>>();
    trainingPathsTranslation.forEach(async trainingPathTranslation => {
      let currentPromise = updateTrainingPathTranslationSupport(support.get(trainingPathTranslation.language));
      promises.push(currentPromise);
      const data = await currentPromise;
      trainingPathTranslation.supportPath = data.filePath;
      trainingPathTranslation.supportUrl = data.fileUrl;
    });
    return Promise.all(promises).then(() => Promise.resolve(trainingPathsTranslation));
  };

  const updateTrainingPath = (trainingPath: TrainingPathDto): Promise<void> => {
    return httpClient.current?.put(`/trainingPath`, trainingPath, {
      headers: { "Content-Type": "application/json" },
    });
  };

  const createTrainingPathTranslation = (trainingPathCreationDTO: TrainingPathCreationDTO): void => {
    httpClient.current
      ?.post(`/trainingPathTranslation`, trainingPathCreationDTO, {
        headers: { "Content-Type": "application/json" },
      })
      .then(() => {
        setLoader(false);
        cancel();
      });
  };

  const updateTrainingPathTranslation = (trainingPathTranslation: TrainingPathTanslationDto): void => {
    httpClient.current
      ?.put(`/trainingPathTranslation`, trainingPathTranslation, {
        headers: { "Content-Type": "application/json" },
      })
      .then(() => {
        setLoader(false);
        cancel();
      });
  };

  const updateTrainingPathTranslationSupport = (support: any): Promise<{ filePath: string; fileUrl: string }> => {
    let formData = new FormData();
    formData.append("support", support);
    return httpClient.current
      ?.post(`/trainingPathTranslation/support`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(response => response.data);
  };

  const getTrainingPathsTranslationByTrainingPath = (): void => {
    httpClient.current?.get(`/trainingPathTranslation/trainingPath/${params.id}`).then(response => {
      const trainingPathsTranslation: Array<TrainingPathTanslationDto> = response.data;
      setTrainingPathsTranslation(trainingPathsTranslation);
      setIsDisplayedFrenchForm(trainingPathsTranslation.some(training => training.language === "FR"));
      setIsDisplayedEnglishForm(trainingPathsTranslation.some(training => training.language === "EN"));
      if (!isDisplayedFrenchForm) {
        setRemainingLang("FR");
      }

      if (!isDisplayedEnglishForm) {
        setRemainingLang("EN");
      }
    });
  };

  const updateTrainingPathTranslations = () => {
    trainingPathsTranslation.forEach(async trainingPathTranslation => {
      trainingPathTranslation.trainingPathID = trainingPath.id;
      if (trainingPathTranslation.id) {
        if (!trainingPathTranslation.supportPath) {
          const data = await updateTrainingPathTranslationSupport(support.get(trainingPathTranslation.language));
          trainingPathTranslation.supportPath = data.filePath;
          trainingPathTranslation.supportUrl = data.fileUrl;
        }
        updateTrainingPathTranslation(trainingPathTranslation);
      } else {
        const trainingPathCreationDTO = new TrainingPathCreationDTO();
        trainingPathCreationDTO.trainingPathDto = trainingPath;
        const data = await updateTrainingPathTranslationSupport(support.get(trainingPathTranslation.language));
        trainingPathTranslation.supportPath = data.filePath;
        trainingPathTranslation.supportUrl = data.fileUrl;
        trainingPathCreationDTO.trainingPathTranslationDto = Array.of(trainingPathTranslation);
        createTrainingPathTranslation(trainingPathCreationDTO);
      }
    });
  };

  const submit = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setLoader(true);
    if (params.id) {
      if(tpTranslationIdToDelete){
        deleteTPTranslationById(tpTranslationIdToDelete);
      }
      if (image) {
        if (imageAccepted) {
          let formData = new FormData();
          formData.append("file", image);
          httpClient.current
            ?.post(`/trainingPath/picture`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
            .then(response => {
              deleteTrainingPathImage(trainingPath.fullImagePath).then(() => {
                trainingPath.imageUrl = response.data?.fileUrl;
                trainingPath.fullImagePath = response.data?.filePath;

                updateTrainingPath(trainingPath).then(() => {
                  updateTrainingPathTranslations();
                  setLoader(false);
                });
              });
            });
        } else {
          setLoader(false);
          toast.error(String(props.t("image_dimension_error_message")));
        }
      } else {
        updateTrainingPath(trainingPath).then(() => {
          updateTrainingPathTranslations();
          setLoader(false);
        });
      }
      
    } else {
      if (imageAccepted) {
        const formData = new FormData();
        formData.append("file", image);
        httpClient.current
          ?.post(`/trainingPath/picture`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then(async response => {
            let training: TrainingPathDto = new TrainingPathDto();
            training.imageUrl = response.data?.fileUrl;
            training.fullImagePath = response.data?.filePath;
            training.status = false;
            let trainingPathCreationDTO = new TrainingPathCreationDTO();
            trainingPathCreationDTO.trainingPathDto = training;
            const trainingPathTranslations = await addSupportToTPS();
            trainingPathCreationDTO.trainingPathTranslationDto = trainingPathTranslations;
            httpClient.current
              ?.post(`/trainingPath`, trainingPathCreationDTO, {
                headers: { "Content-Type": "application/json" },
              })
              .then(() => {
                setLoader(false);
                cancel();
              });
          });
      } else {
        setLoader(false);
        toast.error(String(props.t("image_dimension_error_message")));
      }
    }
  };

  const cancel = () => {
    history.push(`/training-paths`);
  };

  const handleNext = (): void => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = (): void => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const addTrainingPathTranslation = (trainingPathTranslationData: TrainingPathTanslationDto): void => {
    const index = trainingPathsTranslation.indexOf(trainingPathsTranslation.find(trainingPath => trainingPath.language === trainingPathTranslationData.language));
    if (index > -1) {
      trainingPathsTranslation[index] = trainingPathTranslationData;
      setTrainingPathsTranslation(trainingPathsTranslation);
    } else {
      setTrainingPathsTranslation([...trainingPathsTranslation, trainingPathTranslationData]);
    }
  };

  const getTrainingPerLanguage = (language: LanguageOptions): TrainingPathTanslationDto => {
    return trainingPathsTranslation.find(training => training.language === language.value);
  };

  const handleOnChangeDisplayEnglishForm = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setIsDisplayedEnglishForm(event.target.checked);
    setTrainingPathsTranslation(trainingPathsTranslation.filter(training => training.language === "FR"));
    setActiveStep(0);
    const trPTodelete = trainingPathsTranslation.find(training => training.language === "EN")
    if(!event.target.checked && trPTodelete !== undefined && trPTodelete.id > 0){
      setTpTranslationIdToDelete(trPTodelete.id);
    }
  };

 

  const handleOnChangeDisplayFrenchForm = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setIsDisplayedFrenchForm(event.target.checked);
    setTrainingPathsTranslation(trainingPathsTranslation.filter(training => training.language === "EN"));
    setActiveStep(0);
    const trPTodelete = trainingPathsTranslation.find(training => training.language === "FR")
    if(!event.target.checked && trPTodelete !== undefined && trPTodelete.id > 0){
      setTpTranslationIdToDelete(trPTodelete.id);
    }
  };

  const deleteTPTranslationById = (trainingPathTranslationID: number): void => {
    httpClient.current?.delete(`/trainingPathTranslation/${trainingPathTranslationID}`);
  };

  const disableSubmitButton = (): boolean => {
    if (!image && !params.id) return true;
    if (!isDisplayedEnglishForm && isDisplayedFrenchForm && activeStep !== 1) return true;
    if (isDisplayedEnglishForm && !isDisplayedFrenchForm && activeStep !== 1) return true;
    if (isDisplayedEnglishForm && isDisplayedFrenchForm && activeStep !== 2) return true;
    return false;
  };

  const isDispalyedBackButton = (): boolean => {
    if (!isDisplayedEnglishForm && isDisplayedFrenchForm && activeStep === 1) return true;
    if (isDisplayedEnglishForm && !isDisplayedFrenchForm && activeStep === 1) return true;
    if (isDisplayedEnglishForm && isDisplayedEnglishForm && activeStep === 2) return true;
    return false;
  };

  const handleChangeStatus = (support: any, lang: string) => {
    setSupport(state => {
      const newSupport = new Map(state);
      newSupport.set(lang, support);
      return newSupport;
    });
  };

  const getCurrent = language => {
    return support && support.get(language.value) ? support.get(language.value).name : null;
  };

  var _URL = window.URL || window.webkitURL;

  const change = image => {
    var file, img;

    if ((file = image)) {
      img = new Image();
      var objectUrl = _URL.createObjectURL(file);

      img.onload = function () {
        setImageAccepted(this.width.toString() === "1280" && this.height.toString() === "512");
        if (this.width.toString() !== "1280" && this.height.toString() !== "512") {
          toast.error(String(props.t("image_dimension_error_message")));
        }
        _URL.revokeObjectURL(objectUrl);
      };

      img.src = objectUrl;
    }
  };

  return (
    <>
      <Toaster
        position="bottom-right"
        reverseOrder={false}
        toastOptions={{
          duration: 2000,
        }}
      />
      {loader ? (
        <SpinnerComponent />
      ) : (
        <CardBody>
          <Row>
            <Col md={12}>
              <Card>
                <CardBody>
                  <Row>
                    <Col md={12}>
                      <h6>{props.t("choice_language_of_cycle")}</h6>
                      <FormGroup className="m-t-15 m-checkbox-inline mb-0 custom-radio-ml">
                        <div className="checkbox checkbox-dark">
                          <Input id="inline-1" type="checkbox" checked={isDisplayedFrenchForm} onChange={handleOnChangeDisplayFrenchForm} disabled={trainingPath.status && remainingLang !== "FR"} />
                          <Label for="inline-1">Français</Label>
                        </div>
                        <div className="checkbox checkbox-dark">
                          <Input id="inline-2" type="checkbox" checked={isDisplayedEnglishForm} onChange={handleOnChangeDisplayEnglishForm} disabled={trainingPath.status && remainingLang !== "EN"} />
                          <Label for="inline-2">Anglais</Label>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                  {(isDisplayedFrenchForm || isDisplayedEnglishForm) && (
                    <div className={styles.trainingPathStepper}>
                      <Row>
                        <Col md={12}>
                          <Stepper activeStep={activeStep} orientation="vertical" className={styles["stepper_container"]}>
                            {LANGUAGE_OPTIONS.map(language => {
                              return (
                                ((language.value === "FR" && isDisplayedFrenchForm) || (language.value === "EN" && isDisplayedEnglishForm)) && (
                                  <Step key={language.value}>
                                    <StepLabel StepIconComponent={ColorlibStepIcon}>
                                      {currentLang === "fr" && `${props.t("training_path_in")} ${language.label === "français" ? "français" : "anglais"}`}
                                      {currentLang === "en" && `${props.t("training_path_in")} ${language.label === "français" ? "french" : "english"}`}
                                      &nbsp;
                                      <i id={styles["step_icon"]} className={getFlag(Language[language.value])}></i>
                                    </StepLabel>
                                    <StepContent>
                                      <TrainingPathTranslationForm
                                        trainingTranslation={getTrainingPerLanguage(language)}
                                        handleBack={handleBack}
                                        handleNext={handleNext}
                                        activeStep={activeStep}
                                        language={Language[language.value]}
                                        isAllLanguages={isDisplayedFrenchForm && isDisplayedEnglishForm}
                                        addTrainingPathTranslation={addTrainingPathTranslation}
                                        disabled={trainingPath.status && language.value !== remainingLang}
                                        handleSupportChange={support => handleChangeStatus(support, language.value)}
                                        currentSupportName={getCurrent(language)}
                                      />
                                    </StepContent>
                                  </Step>
                                )
                              );
                            })}
                          </Stepper>
                          {isDispalyedBackButton() && <ButtonComponent onClick={() => handleBack()} disable={false} buttonType="button" buttonText={String(props.t("previous"))} save={false} />}
                        </Col>
                      </Row>
                    </div>
                  )}

                  <Row>
                    {!trainingPath.status && (
                      <Col md={12}>
                        <ImageUploader
                          singleImage={true}
                          withIcon={false}
                          withPreview={true}
                          buttonClassName={styles["image_uploader"]}
                          label=""
                          buttonText={String(props.t("image_representative"))}
                          onChange={picture => {
                            if (picture[0] !== undefined) {
                              change(picture[0]);
                              setImage(picture[0]);
                            } else {
                              setImage(null);
                            }
                          }}
                          imgExtension={[".jpg", ".gif", ".png", ".gif", ".svg", ".jpeg"]}
                          maxFileSize={104857600}
                          fileSizeError="la taille du fichier est trop grande"
                        />
                      </Col>
                    )}

                    {!image && trainingPath.imageUrl && (
                      <Col md={12}>
                        <div className={styles["training-path-image"]}>
                          <img src={trainingPath.imageUrl} alt="" />
                        </div>
                      </Col>
                    )}
                  </Row>

                  <Row className={styles["buttons_position"]}>
                    <ButtonComponent onClick={cancel} disable={false} buttonType="button" buttonText={String(props.t("signup_cancel"))} save={false} />
                    <ButtonComponent onClick={submit} disable={disableSubmitButton()} buttonType="button" buttonText={String(props.t("save"))} save={true} />
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </CardBody>
      )}
    </>
  );
};

export default translate(AddTrainingPathForm);
